import React, { useEffect, useState } from "react";
import { FaGlobe, FaExclamationTriangle, FaQuestionCircle, FaCheck, FaRegCircle, FaRobot, FaSpinner, FaChartBar } from "react-icons/fa";
import { SKILLS_MAP } from "../appShared";

export default function QualifyTab({ lead, updateLead, qualifyWithAI, aiQualifying, setDetailTab, searching }) {
  const [selected, setSelected] = useState(lead.skills || []);
  const [expandedCat, setExpandedCat] = useState(null);

  useEffect(() => setSelected(lead.skills || []), [lead.id, lead.skills]);

  const websiteUrl = lead.website ? (lead.website.startsWith("http") ? lead.website : `https://${lead.website}`) : "";
  let websiteDomain = "";
  if (websiteUrl) {
    try { websiteDomain = new URL(websiteUrl).hostname.replace("www.", ""); } catch { websiteDomain = lead.website; }
  }
  const websiteSearch = encodeURIComponent([lead.name, lead.city, lead.country, "official website"].filter(Boolean).join(" "));

  const openExternal = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const toggleSkill = (skill) => {
    const next = selected.includes(skill) ? selected.filter((value) => value !== skill) : [...selected, skill];
    setSelected(next);
    updateLead(lead.id, { skills: next });
  };

  const checks = [
    { label: "Website found", done: (lead.websiteStatus === "found") || !!lead.website },
    { label: "AI analysed", done: !!lead.aiSummary },
    { label: "Decision set", done: !!(lead.decision && lead.decision !== "Undecided") },
    { label: "Value estimated", done: !!lead.projectValue },
    { label: "Skills tagged", done: selected.length > 0 },
  ];
  const doneCount = checks.filter((check) => check.done).length;
  const completion = Math.round((doneCount / checks.length) * 100);

  return (
    <div className="qualify-tab">
      <div className="qualify-hero">
        <div>
          <div className={`website-pill ${(lead.websiteStatus === "found" || lead.website) ? "live" : lead.websiteStatus === "missing" ? "missing" : "missing"}`}>
            {(lead.websiteStatus === "found" || lead.website)
              ? <><FaGlobe style={{ marginRight: 6 }} />Website Live</>
              : lead.websiteStatus === "missing"
                ? <><FaExclamationTriangle style={{ marginRight: 6 }} />No Website Confirmed</>
                : <><FaQuestionCircle style={{ marginRight: 6 }} />Website Unknown</>}
          </div>
          <div className="website-subtext">{(lead.websiteStatus === "found" || lead.website) ? websiteDomain : lead.websiteStatus === "missing" ? "This is usually a strong pitch opportunity" : "Website not verified yet"}</div>
          <div className="qualify-hero-actions">
            {lead.website && <button className="btn-ghost btn-sm" onClick={() => openExternal(websiteUrl)}>Open website</button>}
            <button className="btn-ghost btn-sm" onClick={() => openExternal(`https://www.google.com/search?q=${websiteSearch}`)}>Search website</button>
            {lead.websiteStatus === "missing" && <button className="btn-ghost btn-sm" onClick={() => setDetailTab("info")}>Add website notes</button>}
          </div>
        </div>
        <div className="qualify-score-card">
          <div className="qualify-score-num">{completion}%</div>
          <div className="qualify-score-label">Qualification completeness</div>
        </div>
      </div>

      <div className="qualify-progress">
        {checks.map((check, index) => (
          <div key={index} className={`qualify-check ${check.done ? "done" : ""}`}>
            <span className="qualify-check-icon">{check.done ? <FaCheck /> : <FaRegCircle />}</span>
            <span>{check.label}</span>
          </div>
        ))}
      </div>

      <div className="qualify-section">
        <div className="qualify-section-header">
          <span className="qualify-section-title"><FaRobot style={{ marginRight: 6 }} />Step 1 — AI Analysis</span>
          <span className="qualify-section-hint">Auto-fills decision &amp; value</span>
        </div>
        <button className="btn-ai" onClick={() => qualifyWithAI(lead)} disabled={aiQualifying}>
          {aiQualifying ? <span><FaSpinner className="spin" /> Analysingâ€¦</span> : lead.aiSummary ? "Re-analyse with AI" : "AI Qualify this Lead"}
        </button>
        {lead.aiRecommendation && <div className={`ai-rec ai-rec-${lead.aiRecommendation}`}>AI recommends: <strong>{lead.aiRecommendation?.replace("_", " ")}</strong></div>}
        {lead.aiSummary && (
          <div className="ai-summary-box">
            <div className="ai-summary-text">{lead.aiSummary}</div>
            <div className="ai-metrics-row">
              <span className="ai-metric">Score: <strong>{lead.aiScore ?? "-"}/100</strong></span>
              <span className="ai-metric">Project: <strong>{lead.aiProjectType || "-"}</strong></span>
              <span className="ai-metric">Est. value: <strong>{lead.aiValue || "-"}</strong></span>
            </div>
            {Array.isArray(lead.aiReasons) && lead.aiReasons.length > 0 && (
              <div className="ai-reason-list">
                {lead.aiReasons.map((reason, index) => <div key={index} className="ai-reason-item">â€¢ {reason}</div>)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="qualify-section">
        <div className="qualify-section-header">
          <span className="qualify-section-title"><FaChartBar style={{ marginRight: 6 }} />Step 2 — Your Decision</span>
          <span className="qualify-section-hint">Will you pursue this?</span>
        </div>
        <div className="decision-buttons">
          {["Do it", "Refer out", "Undecided"].map((decision) => (
            <button key={decision} className={`decision-btn ${lead.decision === decision ? "active" : ""}`} onClick={() => updateLead(lead.id, { decision })}>
              {decision}
            </button>
          ))}
        </div>
      </div>

      <div className="qualify-section">
        <div className="qualify-section-header">
          <span className="qualify-section-title">ðŸ’° Step 3 — Project Value</span>
          <span className="qualify-section-hint">Estimated deal size</span>
        </div>
        <div className="value-buttons">
          {["Small", "Medium", "Large"].map((value) => (
            <button key={value} className={`value-btn ${lead.projectValue === value ? "active" : ""}`} onClick={() => updateLead(lead.id, { projectValue: value })}>
              {value === "Small" ? "< 10k" : value === "Medium" ? "10â€“50k" : "50k+"}
            </button>
          ))}
        </div>
      </div>

      <div className="qualify-section">
        <div className="qualify-section-header">
          <span className="qualify-section-title">ðŸ›  Step 4 — Relevant Skills</span>
          {selected.length > 0 ? <span className="skill-count-badge">{selected.length} tagged</span> : <span className="qualify-section-hint">Tag what you can offer</span>}
        </div>
        {Object.entries(SKILLS_MAP).map(([category, skills]) => {
          const catSelected = skills.filter((skill) => selected.includes(skill));
          const isExpanded = expandedCat === category || catSelected.length > 0;
          return (
            <div key={category} className="skill-category">
              <button className={`skill-cat-toggle ${catSelected.length > 0 ? "has-selection" : ""}`} onClick={() => setExpandedCat(isExpanded && expandedCat === category ? null : category)}>
                <span className="skill-cat-label">{category}</span>
                {catSelected.length > 0 && <span className="skill-cat-count">{catSelected.length}</span>}
                <span className="skill-cat-arrow">{isExpanded ? "â–²" : "â–¼"}</span>
              </button>
              {isExpanded && (
                <div className="skill-chips">
                  {skills.map((skill) => (
                    <button key={skill} className={`skill-chip ${selected.includes(skill) ? "active" : ""}`} onClick={() => toggleSkill(skill)}>
                      {skill}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {doneCount >= 2 && <button className="btn-qualify-next" onClick={() => setDetailTab("outreach")}>âœ‰ Ready — Generate Outreach Message â†’</button>}
    </div>
  );
}